import ActionButton from "#/components/buttons/ActionButton";
import type { MapDataItem } from "#/types/items";
import { sanitize, sanitizeItemStatusLabel } from "#/utils/StringUtils";
import { IconArrowUpRight } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";

interface StockAlertPinCardProps {
  item: MapDataItem;
  /** Pin colour of the item's status, drawn as the header dot. */
  color: string;
}

interface CardRow {
  label: string;
  value: string;
  href?: string;
}

/** Non-empty trimmed string, or null so the row is skipped. */
const text = (value: string | null | undefined): string | null => {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

const NEW_TAB = { target: "_blank", rel: "noopener noreferrer" } as const;

/**
 * Callout shown when a Stock Alerts pin is clicked. Solid, readable card:
 * identity and status on top with an arrow that opens the item, one row per
 * known fact (category, client and site link to their pages). Anything unknown
 * is left out rather than shown as "--".
 */
const StockAlertPinCard = ({ item, color }: StockAlertPinCardProps) => {
  const { t } = useTranslation();

  const externalId = text(item.external_id);
  const rfid = text(item.rfid_tags?.[0]?.rfid);
  const title = externalId ?? rfid ?? t("dashboard.stockAlerts.untitled", { id: item.id });
  const statusLabel = text(item.status) ? sanitizeItemStatusLabel(item.status) : null;

  const subClientName = text(item.sub_client?.name);
  const maintenance = text(item.maintenance_status);

  const candidateRows: (CardRow | null)[] = [
    (() => {
      const value = text(item.product?.name);
      return value && item.product
        ? {
            label: t("dashboard.stockAlerts.category"),
            value,
            href: `/inventory/categories/${item.product.id}`,
          }
        : null;
    })(),
    maintenance ? { label: t("dashboard.stockAlerts.maintenance"), value: sanitize(maintenance) } : null,
    subClientName && item.sub_client
      ? {
          label: t("dashboard.stockAlerts.customer"),
          value: subClientName,
          href: `/sub-clients/${item.sub_client.id}`,
        }
      : null,
    (() => {
      const value = text(item.site?.name);
      return value && item.site
        ? { label: t("dashboard.stockAlerts.site"), value, href: `/sites/${item.site.id}` }
        : null;
    })(),
    (() => {
      const value = text(item.location);
      return value ? { label: t("dashboard.stockAlerts.location"), value } : null;
    })(),
    // The RFID is the title when there is no external id, so only repeat it otherwise.
    rfid && title !== rfid ? { label: t("dashboard.stockAlerts.rfid"), value: rfid } : null,
  ];
  const rows = candidateRows.filter((row): row is CardRow => row !== null);

  return (
    <div className="relative min-w-64 max-w-80 rounded-xl border border-gray-200 bg-white p-4 text-sm shadow-lg">
      <div className="flex items-start gap-3">
        <span
          className="mt-1.5 h-3 w-3 shrink-0 rounded-full ring-2 ring-white"
          style={{ backgroundColor: color }}
          aria-hidden
        />
        <div className="flex min-w-0 flex-1 flex-col">
          <span className="truncate text-base font-semibold text-gray-900" title={title}>
            {title}
          </span>
          {statusLabel && <span className="text-xs font-medium text-gray-600">{statusLabel}</span>}
        </div>
        <ActionButton
          icon={IconArrowUpRight}
          variant="subtle"
          size="sm"
          className="-mr-1 -mt-1 shrink-0"
          aria-label={t("dashboard.stockAlerts.openItem")}
          title={t("dashboard.stockAlerts.openItem")}
          onClick={() => window.open(`/inventory/items/${item.id}`, "_blank", "noopener,noreferrer")}
        />
      </div>

      {rows.length > 0 && (
        <dl className="mt-3 flex flex-col gap-2 border-t border-gray-100 pt-3">
          {rows.map((row) => (
            <div key={row.label} className="flex items-baseline justify-between gap-4">
              <dt className="shrink-0 text-gray-500">{row.label}</dt>
              <dd className="min-w-0 break-words text-right font-medium text-gray-900">
                {row.href ? (
                  <a
                    href={row.href}
                    {...NEW_TAB}
                    className="text-blue-600 underline-offset-2 hover:underline"
                  >
                    {row.value}
                  </a>
                ) : (
                  row.value
                )}
              </dd>
            </div>
          ))}
        </dl>
      )}

      {/* Callout pointer towards the pin. */}
      <span
        className="absolute -bottom-2 left-1/2 h-4 w-4 -translate-x-1/2 rotate-45 border-b border-r border-gray-200 bg-white"
        aria-hidden
      />
    </div>
  );
};

export default StockAlertPinCard;
